import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useIsMobile } from '../hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Star, ThumbsUp, Share } from 'lucide-react';

interface Review {
  id: number;
  user: string;
  title: string;
  content: string;
  rating: number;
  timestamp: string;
  likes: number;
}

const Resenhas = () => {
  const isMobile = useIsMobile();
  const { toast } = useToast();

  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewContent, setReviewContent] = useState('');
  const [rating, setRating] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user?.fullName || 'Anônimo';

  useEffect(() => {
    // Dois exemplos fixos ao carregar
    const exemplos: Review[] = [
      {
        id: 1,
        user: 'Kakttus Filmes',
        title: '2 Filhos de Francisco',
        content: 'Um filme emocionante que conta a história de Francisco, um lavrador do interior de Goiás que sonha transformar seus dois filhos em uma famosa dupla sertaneja. A jornada da família é marcada por dificuldades e superações, culminando no sucesso de Zezé Di Camargo & Luciano.',
        rating: 5,
        timestamp: '10/05/2025 14:30',
        likes: 24,
      },
      {
        id: 2,
        user: 'Kakttus Filmes',
        title: 'Navio do Zezé',
        content: 'Um registro imperdível do cruzeiro temático comandado por Zezé Di Camargo. Com muita música boa e momentos especiais com os fãs, o documentário mostra como o cantor interage com seu público em um ambiente descontraído.',
        rating: 4,
        timestamp: '11/05/2025 10:20',
        likes: 18,
      },
    ];
    setReviews(exemplos);
  }, []);

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewTitle || !reviewContent || rating === 0) {
      toast({
        title: 'Erro ao enviar resenha',
        description: 'Preencha todos os campos e adicione uma avaliação',
        variant: 'destructive',
      });
      return;
    }

    const timestamp = new Date().toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

    const newReview: Review = {
      id: Date.now(),
      user: userName,
      title: reviewTitle,
      content: reviewContent,
      rating,
      timestamp,
      likes: 0,
    };

    setReviews([newReview, ...reviews]);
    setReviewTitle('');
    setReviewContent('');
    setRating(0);

    toast({
      title: 'Sucesso!',
      description: 'Sua resenha foi enviada e será publicada após moderação',
    });
  };

  const handleLike = (id: number) => {
    const updated = reviews.map((r) =>
      r.id === id ? { ...r, likes: r.likes + 1 } : r
    );
    setReviews(updated);
  };

  const handleShare = (review: Review) => {
    if (navigator.share) {
      navigator.share({
        title: review.title,
        text: review.content,
        url: window.location.href,
      }).catch((err) => {
        toast({
          title: 'Erro ao compartilhar',
          description: err.message || 'Não foi possível compartilhar',
          variant: 'destructive',
        });
      });
    } else {
      toast({
        title: 'Navegador não suporta compartilhamento',
        description: 'Copie o link manualmente.',
      });
    }
  };

  return (
    <div className="bg-[#141414] bg-cover bg-center min-h-screen" style={{ backgroundImage: "url('/fundo_layout.png')" }}>
      <Header />
      <main className="pt-[120px] sm:pt-[120px] md:pt-[130px] p-4">
        <div className="container mx-auto max-w-4xl">
          {!isMobile && (
            <img
              src="/zeze-apontando.png"
              alt="Zezé apontando"
              className="fixed w-[200px] sm:w-[300px] md:w-[400px] hidden md:block left-0 top-[-30%] translate-y-[50%] ml-0 z-0"
            />
          )}

          <h1 className="text-4xl text-white font-bold mb-10">Resenhas</h1>

          {reviews.map((review) => (
            <div key={review.id} className="bg-black/30 p-6 rounded-lg mb-6">
              <h2 className="text-2xl text-white">{review.title}</h2>
              <p className="text-sm text-gray-400 mb-2">
                Por {review.user} – {review.timestamp}
              </p>
              <p className="text-white mb-4">{review.content}</p>
              <div className="flex justify-between items-center">
                <div className="text-yellow-400 text-xl">
                  {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white"
                    onClick={() => handleLike(review.id)}
                  >
                    <ThumbsUp size={16} /> {review.likes}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white"
                    onClick={() => handleShare(review)}
                  >
                    <Share size={16} /> Compartilhar
                  </Button>
                </div>
              </div>
            </div>
          ))}

          <div className="bg-black/20 p-6 rounded-lg mt-10">
            <h2 className="text-2xl text-white mb-4">Envie sua resenha</h2>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <input
                type="text"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                className="w-full p-2 rounded bg-black/30 text-white border border-gray-700"
                placeholder="Título da Resenha"
              />
              <Textarea
                value={reviewContent}
                onChange={(e) => setReviewContent(e.target.value)}
                className="w-full p-2 rounded bg-black/30 text-white border border-gray-700"
                placeholder="Conte o que você achou..."
                rows={5}
              />
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={24}
                    className={`cursor-pointer ${rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'}`}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
              <Button type="submit" className="bg-rustico-brown text-white hover:bg-rustico-brown/90">
                Publicar Resenha
              </Button>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Resenhas;
